import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckCircle2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import ChoiceTile, {
  ChoiceOption,
} from "@components/ChoiceModalCst/ChoiceTile";

export type LOOutcomeEntityType = "activity" | "course" | "path" | "edition";
export type LOOutcomeMode = "add" | "edit";

interface LOOutcomeModalCstProps {
  open: boolean;
  entityType: LOOutcomeEntityType;
  mode: LOOutcomeMode;
  associationOptions?: ChoiceOption[];
  onAssociate?: (key: string) => void;
  onDismiss: () => void;
}

const LOOutcomeModalCst: React.FC<LOOutcomeModalCstProps> = ({
  open,
  entityType,
  mode,
  associationOptions,
  onAssociate,
  onDismiss,
}) => {
  const { t } = useTranslation("lo-outcome-modal");
  const theme = useTheme();

  const hasAssociations =
    mode === "add" &&
    !!associationOptions &&
    associationOptions.length > 0 &&
    !!onAssociate;

  return (
    <Dialog
      open={open}
      onClose={onDismiss}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "12px 12px 0",
        }}
      >
        <IconButton
          onClick={onDismiss}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2.5,
          padding: "0 24px 24px",
          textAlign: "center",
        }}
      >
        <CheckCircle2 size={48} color={theme.palette.primary.main} />

        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.4,
          }}
        >
          {t(`${entityType}.${mode}`)}
        </Typography>

        {hasAssociations && (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                width: "100%",
                textAlign: "left",
              }}
            >
              {associationOptions!.map((opt) => (
                <ChoiceTile
                  key={opt.key}
                  option={opt}
                  onSelect={onAssociate!}
                />
              ))}
            </Box>

            <Typography
              sx={{
                fontSize: "13px",
                color: theme.palette.text.tertiary,
              }}
            >
              {t("helper")}
            </Typography>
          </>
        )}

        {hasAssociations ? (
          <Button
            onClick={onDismiss}
            variant="text"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: theme.palette.primary.main,
            }}
          >
            {t("dismiss-with-association")}
          </Button>
        ) : (
          <Button
            onClick={onDismiss}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": { backgroundColor: theme.palette.primary.dark },
            }}
          >
            {t("dismiss")}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LOOutcomeModalCst;
